import React, { useState } from 'react'
import { Alert, Button, Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import IntlMessages from '../../util/IntlMessages'
import { isDowngrade as checkIsDowngrade, isSwitchingAnnualToMonthly as checkIsSwitchingAnnualToMonthly } from '../../util/plan-helpers'

import { SubscriptionPeriod } from '@vacationtracker/shared/types/billing'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

interface IDowngradeAlert {
  currentPlan: SubscriptionPlan
  currentPeriod: SubscriptionPeriod
  nextPlan: SubscriptionPlan
  nextPeriod: SubscriptionPeriod
  endDatePeriod: String
  cancelChangePlan: () => void
}

export const DowngradeAlert = ({currentPlan, currentPeriod, nextPlan, nextPeriod, endDatePeriod, cancelChangePlan}: IDowngradeAlert): React.ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (currentPlan === nextPlan && currentPeriod === nextPeriod) {
    return <></>
  }

  const isDowngrade = checkIsDowngrade(currentPlan, nextPlan)
  const isSwitchingAnnualToMonthly = checkIsSwitchingAnnualToMonthly(currentPlan, nextPlan, currentPeriod, nextPeriod)

  if (!isDowngrade && !isSwitchingAnnualToMonthly) {
    return <></>
  }

  // Determine the translation key based on the change type
  let translateId = ''
  
  if (isDowngrade) {
    translateId = 'components.switchPlanModal.downgradeMessage'
  } else if (isSwitchingAnnualToMonthly) {
    translateId = 'components.switchPlanModal.switchFromAnnuallyToMonthly'
  }

  if (translateId === '') {
    return <></>
  }

  const showCancelNewPlan = () => {
    setIsModalOpen(true)
  }

  const handleOk = () => {
    setIsModalOpen(false)
    cancelChangePlan()
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <div style={{ marginTop: 20 }}>
        <Alert
          message={
            <>
              <IntlMessages id={translateId} values={{
                currentPlan,
                nextPlan,
                currentPeriod: currentPlan === 'Freemium' ? '' : currentPeriod,
                nextPeriod: nextPlan === 'Freemium' ? '' : nextPeriod,
                endDatePeriod,
                newPlan: nextPlan,
                legacyPlan: () => '',
                strong: (...chunks) => <strong>{chunks}</strong>,
              }} />
              <Button onClick={showCancelNewPlan} style={{ marginLeft: 5 }}>
                <IntlMessages id="components.downgradeAlert.cancelChangeToPlan" values={{
                  nextPlan,
                  nextPeriod: nextPlan === 'Freemium' ? '' : nextPeriod,
                  strong: (...chunks) => <strong>{chunks}</strong>,
                }} />
              </Button>
            </>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      </div>

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            <IntlMessages
              id="components.downgradeAlert.cancelSwitchPlan"
              values={{
                nextPlan,
                nextPeriod: nextPlan === 'Freemium' ? '' : nextPeriod,
                strong: (...chunks) => <strong>{chunks}</strong>,
              }}
            />
          </span>
        }
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={560}
        okType="primary"
        okText={
          <IntlMessages
            id="components.downgradeAlert.yesIWantToStay"
            values={{
              currentPlan,
              currentPeriod: currentPlan === 'Freemium' ? '' : currentPeriod,
              strong: (...chunks) => <strong>{chunks}</strong>,
            }}
          />
        }
        cancelText={
          <IntlMessages
            id="components.downgradeAlert.noIWantToSwitch"
            values={{
              nextPlan,
              nextPeriod: nextPlan === 'Freemium' ? '' : nextPeriod,
              strong: (...chunks) => <strong style={{ marginLeft: 4, marginRight: 4 }}>{chunks}</strong>,
            }}
          />
        }
        okButtonProps={{
          block: true,
          style: { marginLeft: 0, marginTop: 10 },
        }}
        cancelButtonProps={{
          block: true,
        }}
      />
    </>
  )
}

export default DowngradeAlert
